import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createCandidate,
  deleteCandidateDocument,
  hireCandidate,
  listCandidateDocuments,
  listCandidates,
  rejectCandidate,
  updateCandidate,
  uploadCandidateDocuments,
  type CandidateListParams,
  type CandidatePayload,
  type HireCandidatePayload,
} from '@/api/services/candidates.service'
import { getErrorMessage } from '@/lib/getErrorMessage'

export const candidateQueryKeys = {
  all: ['candidates'] as const,
  list: (params?: CandidateListParams) => ['candidates', 'list', params] as const,
  documents: (id: string) => ['candidates', 'documents', id] as const,
}

export function useCandidates(params?: CandidateListParams) {
  return useQuery({
    queryKey: candidateQueryKeys.list(params),
    queryFn: () => listCandidates(params),
    staleTime: 60 * 1000,
  })
}

export function useCandidateDocuments(candidateId: string | null) {
  return useQuery({
    queryKey: candidateQueryKeys.documents(candidateId ?? ''),
    queryFn: () => listCandidateDocuments(candidateId!),
    enabled: !!candidateId,
    staleTime: 60 * 1000,
  })
}

export function useCreateCandidate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CandidatePayload) => createCandidate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateQueryKeys.all })
      toast.success('Candidate saved')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useUpdateCandidate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CandidatePayload> }) =>
      updateCandidate(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateQueryKeys.all })
      toast.success('Candidate updated')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useRejectCandidate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rejectCandidate(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateQueryKeys.all })
      toast.success('Candidate rejected')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useHireCandidate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: HireCandidatePayload }) =>
      hireCandidate(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateQueryKeys.all })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      toast.success('Candidate hired on probation')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useUploadCandidateDocuments(candidateId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      files,
      category,
      documentType,
    }: {
      files: File[]
      category: 'interview-form' | 'candidate-documents'
      documentType?: string
    }) => uploadCandidateDocuments(candidateId!, files, category, documentType),
    onSuccess: () => {
      if (candidateId) {
        queryClient.invalidateQueries({ queryKey: candidateQueryKeys.documents(candidateId) })
      }
      toast.success('Candidate documents uploaded')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useDeleteCandidateDocument(candidateId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => deleteCandidateDocument(candidateId!, documentId),
    onSuccess: () => {
      if (candidateId) {
        queryClient.invalidateQueries({ queryKey: candidateQueryKeys.documents(candidateId) })
      }
      toast.success('Document removed')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
